// @ts-nocheck

// Renders the order-summary panel for a product named in the URL.
//
// Liquid has no access to query strings, so the handle is resolved here and the
// product is pulled from /products/<handle>.js. Everything price- and option-
// related is driven off that payload, which means the dropdowns, the order-detail
// rows, the subtotal and the buy button all stay in sync with the real variant.
class ProductOrderSummary extends HTMLElement {
  constructor() {
    super();
    this.product = null;
    this.optionNames = [];
    this.selected = [];
    this.currentVariant = null;
    this.subtotalCents = 0;
    this.selects = [];
    this.swiperInstance = null;
    this.colorProducts = [];
    this.colorOptionIndex = -1;
  }

  connectedCallback() {
    this.cacheRefs();
    this.readColorProducts();
    this.initSwiper();
    this.bindEditToggle();
    this.bindBuyButton();
    this.bindCopyCode();
    this.bindPageShow();
    this.loadProduct();
  }

  get hasColorProducts() {
    return this.colorProducts.length > 0;
  }

  disconnectedCallback() {
    this.destroySwiper();
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
  }

  cacheRefs() {
    this.optionRows = this.querySelector('[data-pos-option-rows]');
    this.totalEl = this.querySelector('[data-pos-total]');
    this.subtotalEl = this.querySelector('[data-pos-subtotal]');
    this.editWrapper = this.querySelector('[data-pos-edit]');
    this.editPanel = this.querySelector('[data-pos-edit-panel]');
    this.editFields = this.querySelector('[data-pos-edit-fields]');
    this.errorEl = this.querySelector('[data-pos-error]');
    this.buyBtn = this.querySelector('.pos__buy-btn');
    this.copyBtn = this.querySelector('[data-pos-copy-code]');
    this.promoCode = this.querySelector('[data-pos-promo-code]');
    this.adjustments = Array.from(this.querySelectorAll('[data-pos-adjustment]'));

    this.productCard = this.querySelector('[data-pos-product]');
    this.productImage = this.querySelector('[data-pos-product-image]');
    this.productVendor = this.querySelector('[data-pos-product-vendor]');
    this.productTitle = this.querySelector('[data-pos-product-title]');
    this.productPrice = this.querySelector('[data-pos-product-price]');
  }

  /* ---------------------------------------------------------------- product */

  readColorProducts() {
    const node = this.querySelector('[data-pos-color-products]');
    if (!node) return;
    try {
      const parsed = JSON.parse(node.textContent);
      this.colorProducts = Array.isArray(parsed)
        ? parsed.filter((entry) => entry && Array.isArray(entry.variants) && entry.variants.length)
        : [];
    } catch (err) {
      console.warn('[product-order-summary] colourway list could not be parsed', err);
      this.colorProducts = [];
    }
  }

  findColorProduct(handle) {
    return this.colorProducts.find((entry) => entry.handle === handle) || null;
  }

  // A shared "buy this exact thing" link only needs ?variant=<id>: the id identifies
  // one variant of one colourway, so both dropdowns can be derived from it.
  findProductByVariantId(variantId) {
    return (
      this.colorProducts.find((entry) =>
        entry.variants.some((variant) => Number(variant.id) === variantId)
      ) || null
    );
  }

  // Accepts whatever is convenient in a hand-written link: the handle, the dropdown
  // label, or the full product title, in any casing and with spaces or dashes.
  findColorProductByLabel(value) {
    const wanted = this.slugify(value);
    if (!wanted) return null;

    return (
      this.colorProducts.find((entry) =>
        [entry.handle, entry.label, entry.title].some((candidate) => {
          if (candidate == null) return false;
          return this.slugify(candidate) === wanted;
        })
      ) || null
    );
  }

  slugify(value) {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  getUrl() {
    try {
      return new URL(window.location.href);
    } catch (err) {
      return null;
    }
  }

  getUrlValue(param) {
    const url = this.getUrl();
    if (!url) return '';
    return (url.searchParams.get(param) || '').trim();
  }

  getUrlNumber(param) {
    const value = Number(this.getUrlValue(param));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  // Resolution order matters: a variant id pins the exact colourway AND size, so it
  // beats everything. Then an explicit handle, then a colour name. Anything the link
  // does not pin falls back to the first buyable option.
  async loadProduct() {
    const handle = this.getHandleFromUrl();
    let product = null;

    const variantId = this.getUrlNumber(this.dataset.variantParam || 'variant');
    if (variantId) product = this.findProductByVariantId(variantId);

    // The colourway list is already inlined, so a handle that lives in it costs no
    // network request at all. Only handles outside the list fall through to a fetch.
    if (!product && handle) product = this.findColorProduct(handle);

    if (!product) {
      const requestedColor = this.getUrlValue(this.dataset.colorParam || 'color');
      if (requestedColor) product = this.findColorProductByLabel(requestedColor);
    }

    if (!product && handle) {
      product = await this.fetchProduct(handle);
      // Keep it in the dropdown, otherwise the shopper could never get back to the
      // colourway they arrived on after switching away from it.
      if (product) {
        this.colorProducts = this.hasColorProducts
          ? [this.toColorEntry(product), ...this.colorProducts]
          : this.colorProducts;
      }
    }

    if (!product) product = this.colorProducts[0] || this.getFallbackProduct();

    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
      this.showEmptyState(handle);
      return;
    }

    this.setProduct(product);
  }

  // Mirrors the Liquid that labels the inlined colourways: strip the model prefix, then
  // keep only what follows the last separator, so "OB -1 UTILITY BOOT - BLACK AF" reads
  // as "BLACK AF". Used for colourways fetched from the URL, which never pass through
  // that Liquid but sit in the same dropdown.
  buildColorLabel(title) {
    let label = String(title || '').trim();

    const prefix = this.dataset.colorLabelPrefix || '';
    if (prefix && label.startsWith(prefix)) label = label.slice(prefix.length);

    const separator = this.dataset.colorLabelSeparator || '';
    const at = separator ? label.lastIndexOf(separator) : -1;
    if (at > -1) label = label.slice(at + separator.length);

    return label.trim();
  }

  // Normalises a /products/<handle>.js payload into the shape the inlined list uses.
  toColorEntry(product) {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      label: this.buildColorLabel(product.title),
      price: product.price,
      vendor: product.vendor,
      featured_image: this.resolveImageSrc(product.featured_image) || this.resolveImageSrc(product.images?.[0]),
      options: this.getOptionNames(product),
      images: Array.isArray(product.images) ? product.images.slice(0, 6) : [],
      variants: product.variants.map((variant) => ({
        id: variant.id,
        options: variant.options,
        price: variant.price,
        available: variant.available,
      })),
    };
  }

  // Priority: ?handle= in the query string, then a /products/<handle> path so the
  // section also works when dropped straight onto a product template.
  getHandleFromUrl() {
    let url;
    try {
      url = new URL(window.location.href);
    } catch (err) {
      return '';
    }

    const param = this.dataset.handleParam || 'handle';
    const fromQuery = url.searchParams.get(param);
    if (fromQuery && fromQuery.trim()) return this.normalizeHandle(fromQuery);

    const match = url.pathname.match(/\/products\/([^/?#]+)/);
    if (match) return this.normalizeHandle(decodeURIComponent(match[1]));

    return '';
  }

  // Tolerates a bare handle, a /products/<handle> path, or a full product URL being
  // passed in the query param, so links can be built however is convenient.
  normalizeHandle(value) {
    const withoutHost = String(value).trim().replace(/^https?:\/\/[^/]+/i, '');
    const withoutQuery = withoutHost.split(/[?#]/)[0];
    const parts = withoutQuery.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  }

  async fetchProduct(handle) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (err) {
      console.warn('[product-order-summary] could not load product:', handle, err);
      return null;
    }
  }

  getFallbackProduct() {
    const node = this.querySelector('[data-pos-fallback-product]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }
  }

  setProduct(product, snapshot = null) {
    this.product = product;
    this.optionNames = this.getOptionNames(product);

    // Only meaningful when colourways are products: the product's own Color option
    // is then redundant and gets hidden from both the rows and the dropdowns.
    this.colorOptionIndex = this.hasColorProducts
      ? this.optionNames.findIndex((name) => /colou?r/i.test(String(name)))
      : -1;

    this.selected = snapshot ? this.buildSelection(snapshot) : this.getInitialSelection();

    this.fillTokens();
    this.renderProductCard();
    this.renderProductSlides();
    this.renderEditPanel();
    this.classList.remove('pos--loading');
    this.update();
  }

  // Captured before a colour switch and reapplied after, keyed by option NAME rather
  // than index — two colourway products need not order their options the same way.
  getSelectionSnapshot() {
    const snapshot = {};
    this.optionNames.forEach((name, index) => {
      if (index === this.colorOptionIndex) return;
      snapshot[String(name).toLowerCase()] = this.selected[index];
    });
    return snapshot;
  }

  buildSelection(snapshot) {
    const base =
      this.product.variants.find((variant) => variant.available) || this.product.variants[0];
    const fallback = base ? base.options.slice() : [];
    if (!snapshot) return fallback;

    const candidate = fallback.slice();
    this.optionNames.forEach((name, index) => {
      const wanted = snapshot[String(name).toLowerCase()];
      if (wanted == null) return;
      const match = this.getOptionValues(index).find(
        (value) => String(value).toLowerCase() === String(wanted).toLowerCase()
      );
      if (match) candidate[index] = match;
    });

    // Carrying the size over is a convenience, not a promise — if this colourway does
    // not have it in stock, fall back to something buyable rather than landing on a
    // sold-out combination.
    const variant = this.findVariant(candidate);
    return variant && variant.available ? candidate : fallback;
  }

  async handleColorChange(handle) {
    if (!handle || handle === this.product.handle) return;

    const snapshot = this.getSelectionSnapshot();
    let next = this.findColorProduct(handle);
    if (!next) next = await this.fetchProduct(handle);
    if (!next || !Array.isArray(next.variants) || !next.variants.length) return;

    this.setProduct(next, snapshot);
    this.syncUrl();
  }

  // Rewrites the address bar to describe exactly what is on screen, so the URL is
  // always copy-pasteable and reproduces this colourway and size on landing.
  //
  // ?variant= and the colour param are dropped rather than updated: both are alternate
  // ways of saying what handle already says, and a stale one left behind would
  // contradict the rest on the next load.
  syncUrl() {
    if (!this.product) return;

    const url = this.getUrl();
    if (!url) return;

    url.searchParams.set(this.dataset.handleParam || 'handle', this.product.handle);
    url.searchParams.delete(this.dataset.variantParam || 'variant');
    url.searchParams.delete(this.dataset.colorParam || 'color');

    this.optionNames.forEach((name, index) => {
      if (index === this.colorOptionIndex) return;
      const value = this.selected[index];
      if (value == null || value === '') return;
      url.searchParams.set(this.optionParamName(name), value);
    });

    try {
      window.history.replaceState({}, '', url.toString());
    } catch (err) {
      /* history is a convenience here, never required for the flow */
    }
  }

  getOptionNames(product) {
    if (!Array.isArray(product.options)) return [];
    // /products/<handle>.js returns option objects on newer API versions and plain
    // strings on older ones; Liquid's `| json` returns strings.
    return product.options.map((option) => (typeof option === 'string' ? option : option.name));
  }

  // Always derived from the variants rather than option.values, so a value can never
  // appear in the dropdown without a variant behind it.
  getOptionValues(index) {
    const values = [];
    this.product.variants.forEach((variant) => {
      const value = variant.options[index];
      if (value != null && !values.includes(value)) values.push(value);
    });
    return values;
  }

  // ?variant=<id> wins, then a per-option param matching the option name
  // (?size=US%2010.5&color=Black), then the first available variant.
  getInitialSelection() {
    const variantId = this.getUrlNumber(this.dataset.variantParam || 'variant');
    if (variantId) {
      const fromId = this.product.variants.find((variant) => Number(variant.id) === variantId);
      if (fromId) return fromId.options.slice();
    }

    const base =
      this.product.variants.find((variant) => variant.available) || this.product.variants[0];
    const selection = base ? base.options.slice() : [];

    // One query param per option, keyed off the option name: a "Size" option reads
    // ?size=, a "Width" option reads ?width=. Matching is slug-based so ?size=us-10-5
    // and ?size=US%2010.5 both land on the same value.
    this.optionNames.forEach((name, index) => {
      if (index === this.colorOptionIndex) return;

      const requested = this.getUrlValue(this.optionParamName(name));
      if (!requested) return;

      const wanted = this.slugify(requested);
      const match = this.getOptionValues(index).find((value) => this.slugify(value) === wanted);
      if (match) selection[index] = match;
    });

    return selection;
  }

  optionParamName(name) {
    return this.slugify(name);
  }

  /* ----------------------------------------------------------------- render */

  update() {
    this.currentVariant = this.findVariant(this.selected);
    this.renderProductCardPrice();
    this.renderOptionRows();
    this.renderTotals();
    this.syncSelects();
    this.updateBuyButton();
  }

  findVariant(selection) {
    if (!selection.length) return null;
    return (
      this.product.variants.find((variant) =>
        selection.every((value, index) => variant.options[index] === value)
      ) || null
    );
  }

  /* ------------------------------------------------------------ product card */

  // Image, vendor and title of the resolved product. Split from the price so a
  // size change repaints only the price, while a colourway switch repaints the lot.
  renderProductCard() {
    if (!this.productCard) return;

    const src = this.resolveImageSrc(this.product.featured_image) ||
      this.resolveImageSrc(this.product.images?.[0]);

    if (this.productImage) {
      if (src) {
        this.productImage.src = this.thumbUrl(src);
        this.productImage.alt = this.product.title || '';
        this.productImage.hidden = false;
      } else {
        this.productImage.removeAttribute('src');
        this.productImage.hidden = true;
      }
    }

    if (this.productVendor) {
      this.productVendor.textContent = this.product.vendor || this.dataset.vendorFallback || '';
    }

    if (this.productTitle) this.productTitle.textContent = this.product.title || '';

    this.renderProductCardPrice();
  }

  // The undiscounted price of what is currently selected, so the card agrees with the
  // Total Price row rather than with the product's cheapest variant.
  renderProductCardPrice() {
    if (!this.productPrice || !this.product) return;
    const cents = this.currentVariant ? this.currentVariant.price : this.product.price || 0;
    this.productPrice.textContent = this.formatMoney(cents);
  }

  // featured_image is a URL string on /products/<handle>.js and in Liquid's product
  // JSON, but an object on the media-style payloads, so accept both.
  resolveImageSrc(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || '';
  }

  // The card renders at ~108px, while raw CDN URLs from a fetched product are full
  // size. Liquid-inlined URLs already carry a width, and are left alone.
  thumbUrl(src) {
    if (!/cdn\.shopify\.com/.test(src) || /[?&]width=/.test(src)) return src;
    return `${src}${src.includes('?') ? '&' : '?'}width=400`;
  }

  renderOptionRows() {
    if (!this.optionRows) return;

    this.optionRows.innerHTML = '';

    this.optionNames.forEach((name, index) => {
      if (index === this.colorOptionIndex) return;
      this.optionRows.appendChild(this.buildRow(name, this.selected[index] || ''));
    });

    if (this.hasColorProducts) {
      const current = this.findColorProduct(this.product.handle);
      this.optionRows.appendChild(
        this.buildRow(
          this.dataset.colorRowLabel || 'Color',
          current ? current.label : this.buildColorLabel(this.product.title)
        )
      );
    }
  }

  buildRow(name, value) {
    const prefix = (this.dataset.optionLabelPrefix || '').trim();

    const row = document.createElement('div');
    row.className = 'pos__row';

    const label = document.createElement('span');
    label.textContent = prefix ? `${prefix} ${name}` : name;

    const valueEl = document.createElement('span');
    valueEl.className = 'pos__row-value';
    valueEl.textContent = value;

    row.append(label, valueEl);
    return row;
  }

  renderTotals() {
    const totalCents = this.currentVariant ? this.currentVariant.price : this.product.price || 0;
    let subtotal = totalCents;

    this.adjustments.forEach((row) => {
      const cents = this.parseAmountToCents(row.dataset.amount);
      const adds = row.dataset.mode === 'add';
      subtotal += adds ? cents : -cents;

      const valueEl = row.querySelector('[data-pos-adjustment-value]');
      if (valueEl) valueEl.textContent = `${adds ? '+' : '-'}${this.formatMoney(cents)}`;
    });

    this.subtotalCents = Math.max(subtotal, 0);

    if (this.totalEl) this.totalEl.textContent = this.formatMoney(totalCents);
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(this.subtotalCents);
  }

  renderEditPanel() {
    if (!this.editFields) return;

    this.editFields.innerHTML = '';
    this.selects = [];

    this.optionNames.forEach((name, index) => {
      if (index === this.colorOptionIndex) return;

      const select = this.buildSelect(name);
      select.addEventListener('change', (event) =>
        this.handleOptionChange(index, event.target.value)
      );

      this.selects.push({ element: select, optionIndex: index });
    });

    if (this.hasColorProducts) {
      const select = this.buildSelect(this.dataset.colorRowLabel || 'Color');
      select.addEventListener('change', (event) => this.handleColorChange(event.target.value));
      this.colorSelect = select;
    } else {
      this.colorSelect = null;
    }
  }

  buildSelect(labelText) {
    const field = document.createElement('div');
    field.className = 'pos__field';

    const select = document.createElement('select');
    select.className = 'pos__select';
    select.setAttribute('aria-label', labelText);

    field.appendChild(select);
    this.editFields.appendChild(field);
    return select;
  }

  syncSelects() {
    const suffix = this.dataset.unavailableSuffix || '';

    this.selects.forEach(({ element, optionIndex }) => {
      element.innerHTML = '';

      this.getOptionValues(optionIndex).forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = this.isValueAvailable(optionIndex, value)
          ? value
          : `${value}${suffix}`;
        element.appendChild(option);
      });

      element.value = this.selected[optionIndex] ?? '';
    });

    if (!this.colorSelect) return;

    this.colorSelect.innerHTML = '';
    this.colorProducts.forEach((entry) => {
      const option = document.createElement('option');
      option.value = entry.handle;
      option.textContent = this.colorwayHasStock(entry) ? entry.label : `${entry.label}${suffix}`;
      this.colorSelect.appendChild(option);
    });
    this.colorSelect.value = this.product.handle;
  }

  colorwayHasStock(entry) {
    return entry.variants.some((variant) => variant.available);
  }

  // Available given everything else currently selected — that is what makes the
  // sold-out suffix track the other dropdown instead of the product as a whole.
  isValueAvailable(index, value) {
    return this.product.variants.some((variant) => {
      if (!variant.available) return false;
      if (variant.options[index] !== value) return false;
      return this.selected.every((selectedValue, i) => i === index || variant.options[i] === selectedValue);
    });
  }

  handleOptionChange(index, value) {
    const next = this.selected.slice();
    next[index] = value;

    // If the new combination has no buyable variant, snap the other options onto the
    // first available variant that still honours what was just picked. Without this
    // the two dropdowns can strand the shopper on a dead combination they can only
    // escape by guessing.
    const exact = this.findVariant(next);
    if (!exact || !exact.available) {
      const rescue = this.product.variants.find(
        (variant) => variant.available && variant.options[index] === value
      );
      if (rescue) {
        this.selected = rescue.options.slice();
        this.update();
        this.syncUrl();
        return;
      }
    }

    this.selected = next;
    this.update();
    this.syncUrl();
  }

  updateBuyButton() {
    if (!this.buyBtn) return;

    const available = Boolean(this.currentVariant && this.currentVariant.available);
    const label = available
      ? (this.dataset.buyLabel || '').split('[price]').join(this.formatMoney(this.subtotalCents))
      : this.dataset.soldOutLabel || 'Sold out';

    this.setButtonLabel(this.buyBtn, label);
    this.buyBtn.disabled = !available;
  }

  // custom-button renders the label twice: the visible span and the data-text copy
  // that slides in on hover. Both have to move together.
  setButtonLabel(button, label) {
    const text = button.querySelector('.custom-button_text');
    const inner = button.querySelector('.custom-button_inner');
    if (text) text.textContent = label;
    if (inner) inner.setAttribute('data-text', label);
  }

  // Re-run on every colour switch, so the untouched markup is stashed the first time
  // and restored before each pass — otherwise the token is consumed once and the
  // heading would keep the title of whichever colourway loaded first.
  fillTokens() {
    const title = this.product?.title || '';
    this.querySelectorAll('[data-pos-tokens]').forEach((element) => {
      if (element.dataset.posTokenSource == null) {
        element.dataset.posTokenSource = element.innerHTML;
      } else {
        element.innerHTML = element.dataset.posTokenSource;
      }
      this.replaceTextToken(element, '[product]', title);
    });
  }

  // Only when the slider is set to follow the product; the image-blocks source leaves
  // the Liquid-rendered slides exactly as they are.
  renderProductSlides() {
    if (this.dataset.sliderSource !== 'product') return;

    const wrapper = this.querySelector('.pos__swiper .swiper-wrapper');
    const images = this.product.images || [];
    if (!wrapper || images.length === 0) return;

    wrapper.innerHTML = '';
    images.forEach((src, index) => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide pos__slide';

      const img = document.createElement('img');
      img.src = src;
      img.alt = this.product.title || '';
      img.loading = index === 0 ? 'eager' : 'lazy';

      slide.appendChild(img);
      wrapper.appendChild(slide);
    });

    if (this.swiperInstance) {
      this.swiperInstance.update();
      this.swiperInstance.slideTo(0, 0);
      this.restartActiveFill();
    }
  }

  // Walks text nodes so the token can sit inside richtext markup without the
  // surrounding tags being re-parsed.
  replaceTextToken(root, token, value) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      if (node.nodeValue.includes(token)) {
        node.nodeValue = node.nodeValue.split(token).join(value);
      }
    });
  }

  showEmptyState(handle) {
    this.classList.remove('pos--loading');
    this.classList.add('pos--empty');

    if (window.Shopify?.designMode) {
      const message = handle
        ? `No product found for handle "${handle}". Set a fallback product in the section settings.`
        : 'No product handle in the URL. Add ?handle=<product-handle> or set a fallback product.';
      this.innerHTML = `<div class="pos__notice">${message}</div>`;
    } else {
      this.hidden = true;
    }
  }

  /* ------------------------------------------------------------------- money */

  parseAmountToCents(raw) {
    const value = parseFloat(String(raw || '').replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(value)) return 0;
    return Math.round(value * 100);
  }

  formatMoney(cents) {
    // Some shops wrap money_format in markup (e.g. <span class=money>${{amount}}</span>).
    // These values go out through textContent, so strip tags rather than print them.
    const format = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = this.toAmount(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = this.toAmount(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = this.toAmount(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = this.toAmount(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = this.toAmount(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = this.toAmount(cents, 0, ' ', ',');
        break;
      default:
        amount = this.toAmount(cents, 2, ',', '.');
    }

    if (this.dataset.hideZeroDecimals === 'true') {
      amount = amount.replace(/[.,]00$/, '');
    }

    return format.replace(placeholder, amount);
  }

  toAmount(cents, decimals, thousandsSeparator, decimalSeparator) {
    const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    return fraction ? `${grouped}${decimalSeparator}${fraction}` : grouped;
  }

  /* --------------------------------------------------------------- checkout */

  /* ------------------------------------------------------------- copy code */

  bindCopyCode() {
    if (!this.copyBtn || !this.promoCode) return;

    this.copyBtn.addEventListener('click', async () => {
      const code = (this.promoCode.textContent || '').trim();
      if (!code) return;

      const copied = await this.writeToClipboard(code);
      if (!copied) return;

      // Label swap is the whole confirmation -- no toast to position and nothing that
      // moves the layout, so the strip stays the same height while it reads "Copied".
      const label = this.copyBtn.querySelector('.pos__promo-copy-text');
      if (!label) return;

      clearTimeout(this.copyResetTimer);
      label.textContent = this.copyBtn.dataset.copiedLabel || 'Copied';
      this.copyBtn.classList.add('is-copied');

      this.copyResetTimer = setTimeout(() => {
        label.textContent = this.copyBtn.dataset.copyLabel || 'Copy';
        this.copyBtn.classList.remove('is-copied');
      }, 2000);
    });
  }

  // navigator.clipboard only exists in secure contexts, so theme previews served over
  // http and older mobile browsers fall back to the selection trick rather than silently
  // doing nothing when the shopper taps Copy.
  async writeToClipboard(text) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      /* fall through to the legacy path below */
    }

    try {
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.top = '-1000px';
      document.body.appendChild(field);
      field.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(field);
      return ok;
    } catch (err) {
      console.error('[product-order-summary] copy failed', err);
      return false;
    }
  }

  // Buy Now navigates away while still showing its spinner, which is right on the way
  // out but wrong on the way back: pressing Back restores the page from the bfcache with
  // the DOM exactly as it was left, so the button comes back grey, spinning and disabled
  // forever -- nothing re-runs on a bfcache restore, not even connectedCallback. pageshow
  // is the one event that does fire, so the button is reset there. updateBuyButton runs
  // after the reset to put back the disabled state a sold-out variant is entitled to.
  bindPageShow() {
    this.onPageShow = () => {
      if (!this.buyBtn) return;
      this.setButtonLoading(this.buyBtn, false);
      if (this.product) this.updateBuyButton();
    };

    window.addEventListener('pageshow', this.onPageShow);
  }

  bindBuyButton() {
    if (!this.buyBtn) return;
    this.buyBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleBuyNow();
    });
  }

  async handleBuyNow() {
    const variant = this.currentVariant;
    if (!variant || !variant.available) return;

    this.hideError();
    this.setButtonLoading(this.buyBtn, true);

    const root = window.Shopify?.routes?.root || '/';

    try {
      let needsAdd = true;

      if (this.dataset.clearCart === 'true') {
        await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
      } else {
        // One pair per visit. The cart may only ever hold a single line from this page,
        // so every other colourway and size it sells is cleared out first: switching from
        // US 8 to US 10 and pressing Buy Now again replaces the old line rather than
        // adding beside it, and pressing it twice on the same variant adds nothing at
        // all. Lines the page does not sell are left alone -- a deposit or anything else
        // the shopper is carrying is not ours to remove.
        needsAdd = await this.reserveSingleLine(root, variant.id);
      }

      if (needsAdd) {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'cart/add.js failed');
        }
      }

      // Straight to checkout. No cart event is dispatched, so the theme's cart
      // drawer stays shut and the shopper lands on checkout with this variant.
      window.location.href = this.buildCheckoutUrl(root);
    } catch (err) {
      console.error('[product-order-summary] buy now failed', err);
      this.showError();
      this.setButtonLoading(this.buyBtn, false);
    }
  }

  // Leaves the cart holding at most one line from this page, the wanted variant at
  // quantity 1, and reports whether that line still has to be added.
  //
  // Returns true (add it) when the cart cannot be read: a duplicate line is recoverable
  // at checkout, an empty cart is a dead end.
  async reserveSingleLine(root, variantId) {
    const cart = await this.fetchCart(root);
    if (!cart) return true;

    const owned = this.getSectionVariantIds();
    const wanted = Number(variantId);
    let keeper = null;

    // Sequential, not Promise.all: the cart endpoints serialise writes anyway and firing
    // them together makes Shopify reject all but one with a 429.
    for (const line of cart.items) {
      const lineVariant = Number(line.variant_id ?? line.id);
      if (!owned.has(lineVariant)) continue;

      if (lineVariant === wanted && !keeper) {
        keeper = line;
        continue;
      }

      await this.changeLineQuantity(root, line.key, 0);
    }

    if (!keeper) return true;

    // An earlier visit, or another page, may have left this variant sitting at more than
    // one. Pull it back to the single pair the panel above the button is priced for.
    if (Number(keeper.quantity) !== 1) {
      await this.changeLineQuantity(root, keeper.key, 1);
    }

    return false;
  }

  // Every variant this section can sell: the whole colourway list, plus the current and
  // fallback products, which can sit outside that list when the URL named a handle of
  // its own. Anything else in the cart belongs to someone else.
  getSectionVariantIds() {
    const ids = new Set();

    const collect = (entry) => {
      if (!entry || !Array.isArray(entry.variants)) return;
      entry.variants.forEach((variant) => {
        if (variant && variant.id != null) ids.add(Number(variant.id));
      });
    };

    this.colorProducts.forEach(collect);
    collect(this.product);
    collect(this.getFallbackProduct());

    return ids;
  }

  // cache: 'no-store' because /cart.js is served with caching headers that would
  // otherwise hand back the cart as it was before the last write, which is exactly the
  // state this must not trust.
  async fetchCart(root) {
    try {
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;

      const cart = await response.json();
      return Array.isArray(cart.items) ? cart : null;
    } catch (err) {
      console.error('[product-order-summary] cart lookup failed', err);
      return null;
    }
  }

  // Addressed by line key rather than index: indexes shift as lines are removed, and the
  // same variant can hold two lines when their properties differ. A failure is logged
  // and swallowed -- losing a stale line is worth less than blocking the sale.
  async changeLineQuantity(root, key, quantity) {
    if (!key) return;

    try {
      await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
    } catch (err) {
      console.error('[product-order-summary] cart change failed', err);
    }
  }

  buildCheckoutUrl(root) {
    const code = (this.dataset.discountCode || '').trim();
    return code ? `${root}checkout?discount=${encodeURIComponent(code)}` : `${root}checkout`;
  }

  setButtonLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      button.setAttribute('disabled', 'true');
    } else {
      button.removeAttribute('disabled');
    }
  }

  showError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = this.dataset.errorText || 'Something went wrong. Please try again.';
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }

  /* ------------------------------------------------------------------- misc */

  bindEditToggle() {
    this.editToggle = this.querySelector('[data-pos-edit-toggle]');
    if (!this.editToggle || !this.editWrapper || !this.editPanel) return;

    // The resting state is applied directly rather than through setEditOpen, so a
    // panel that starts open does not animate itself down on first paint.
    this.editOpen = this.editWrapper.classList.contains('pos__edit--open');
    this.editPanel.style.height = this.editOpen ? 'auto' : '0px';

    // Released back to auto once the opening transition lands. Leaving it pinned to
    // the pixel height it was measured at would clip the panel whenever its contents
    // change afterwards — a colourway switch can add or drop a dropdown — or when the
    // selects rewrap at a narrower width.
    this.editPanel.addEventListener('transitionend', (event) => {
      if (event.target !== this.editPanel || event.propertyName !== 'height') return;
      if (this.editOpen) this.editPanel.style.height = 'auto';
    });

    this.editToggle.addEventListener('click', () => this.setEditOpen(!this.editOpen));
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  // The inner row keeps its full height whatever the clipper is doing, so it is the
  // one thing that can be asked how tall the panel wants to be mid-animation.
  getEditFieldsHeight() {
    if (!this.editFields) return this.editPanel.scrollHeight;
    return this.editFields.getBoundingClientRect().height;
  }

  // height has nothing to interpolate towards or away from `auto`, so each direction
  // is pinned to a concrete pixel value and flushed with a forced reflow before the
  // target is set — otherwise the browser coalesces both writes into one frame and
  // the panel jumps.
  setEditOpen(open) {
    if (!this.editPanel || open === this.editOpen) return;

    this.editOpen = open;
    this.editWrapper.classList.toggle('pos__edit--open', open);
    this.editToggle.setAttribute('aria-expanded', String(open));

    // With motion turned down the CSS drops the transition, so transitionend never
    // arrives to hand the height back to auto. Skip straight to the resting state.
    if (this.prefersReducedMotion()) {
      this.editPanel.style.height = open ? 'auto' : '0px';
      return;
    }

    // Measured off the panel as it currently renders rather than off its resting
    // state, so a click landing mid-animation carries on from where the panel got to
    // instead of snapping to either end first.
    const from = this.editPanel.getBoundingClientRect().height;
    const to = open ? this.getEditFieldsHeight() : 0;

    this.editPanel.style.height = `${from}px`;
    void this.editPanel.offsetHeight;
    this.editPanel.style.height = `${to}px`;
  }

  initSwiper() {
    const swiperEl = this.querySelector('.pos__swiper');
    if (!swiperEl) return;

    // swiper-bundle is loaded with `defer` in theme.liquid, so it can land after
    // this element upgrades. Same retry approach as upcoming-product-waitlist.
    if (typeof window.Swiper === 'undefined') {
      this.swiperRetryCount = (this.swiperRetryCount || 0) + 1;
      if (this.swiperRetryCount > 50) return;
      this.swiperRetry = window.setTimeout(() => this.initSwiper(), 100);
      return;
    }

    this.swiperInstance = new window.Swiper(swiperEl, {
      slidesPerView: 1,
      speed: 500,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      pagination: {
        el: this.querySelector('.pos__dots'),
        clickable: true,
        bulletClass: 'pos__dot',
        bulletActiveClass: 'pos__dot--active',
      },
      on: {
        slideChange: () => this.restartActiveFill(),
      },
    });

    this.restartActiveFill();
  }

  // Swiper only moves the active class between bullets, which does not retrigger the
  // fill animation. Removing and re-adding it after a forced reflow restarts the fill
  // on every slide change. Same approach as upcoming-product-waitlist.
  restartActiveFill() {
    const active = this.querySelector('.pos__dot--active');
    if (!active) return;
    active.classList.remove('pos__dot--active');
    void active.offsetWidth;
    active.classList.add('pos__dot--active');
  }

  destroySwiper() {
    if (this.swiperRetry) window.clearTimeout(this.swiperRetry);
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

if (!customElements.get('product-order-summary')) {
  customElements.define('product-order-summary', ProductOrderSummary);
}
